import { ChangeDetectionStrategy, Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HealthHistoryFilterState, HealthHistoryType } from '@libs/store';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { HealthHistoryDialogService } from '../../health-history-dialog.service';
import { HealthHistoryFilterComponent } from '../health-history-filter/health-history-filter.component';
import { HealthHistoryItemDeleteData, HealthHistoryItemEditData } from '../health-history-item-edit/health-history-item-edit.component';
import { HealthHistorySectionComponent } from '../health-history-section/health-history-section.component';
import { HealthHistoryTabService } from './health-history-tab.service';

@Component({
  selector: 'lib-health-history-tab',
  standalone: true,
  imports: [CommonModule, HealthHistorySectionComponent, LottieComponent, HealthHistoryFilterComponent],
  templateUrl: './health-history-tab.component.html',
  styleUrl: './health-history-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HealthHistoryTabService],
})
export class HealthHistoryTabComponent implements OnInit, OnDestroy {
  private readonly healthHistoryTabService = inject(HealthHistoryTabService);
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);
  public readonly HealthHistoryType = HealthHistoryType;

  public readonly medicalConditionsSection = this.healthHistoryTabService.medicalConditionsSection;
  public readonly allergiesSection = this.healthHistoryTabService.allergiesSection;
  public readonly patientReportedMedicationsSection = this.healthHistoryTabService.patientReportedMedicationsSection;
  public readonly isLoading = this.healthHistoryTabService.isLoading;
  public readonly editedHealthHistoryItemId = this.healthHistoryTabService.editedHealthHistoryItemId;

  public readonly filterState = this.healthHistoryTabService.filterState;

  // Feature flags from parent dialog service
  public readonly addHealthHistoryEnabled = this.healthHistoryDialogService.addHealthHistoryEnabled;
  public readonly editHealthHistoryEnabled = this.healthHistoryDialogService.editHealthHistoryEnabled;
  public readonly deleteHealthHistoryEnabled = this.healthHistoryDialogService.deleteHealthHistoryEnabled;

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  protected setEditMode(event: { editMode: boolean; itemId: string }): void {
    this.healthHistoryTabService.setEditMode(event.editMode, event.itemId);
  }

  protected onDeleteItem(event: HealthHistoryItemDeleteData, type: HealthHistoryType): void {
    this.healthHistoryTabService.deleteHealthHistoryItem(event, type);
  }

  protected onAddNewItem(event: { type: HealthHistoryType }): void {
    this.healthHistoryTabService.addNewHealthHistoryItem(event.type);
  }

  protected onSaveItem(event: HealthHistoryItemEditData, type: HealthHistoryType): void {
    this.healthHistoryTabService.editHealthHistoryItem(event, type);
  }

  protected onDiscardNewItem(): void {
    this.healthHistoryTabService.discardNewHealthHistoryItem();
  }

  public ngOnInit(): void {
    this.healthHistoryTabService.init();
  }

  protected onFilterChanged(filterState: HealthHistoryFilterState): void {
    this.healthHistoryTabService.changeFilter(filterState);
  }

  public ngOnDestroy(): void {
    this.healthHistoryTabService.setEditMode(false);
  }
}
